// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import wamClient from '@messaging/bifrost';
import AppsAnalytics from '../../analytics/AppsAnalytics';
import {subHeadingFilter, goUrlBaseLink, appButtonConfig} from '../../constants/appsConstants';
import {thorConfirmationTypes, analyticsEvents, updateTypes,
	thorAppsStatesCategory, wamMsgStatusType,
	thorEntitlementInfoType,
	learnMoreLinkInConflictBase,
	learnMoreLinkInUpdateBase, ctaActionIdsDenylist} from '../../constants/wamConstants';
import {ACTION_ID} from '@ccxi/ccxi-apps/src/utils/modelMapper/appModelMapper/appModelConstants';
import getConfigApps from '../../config';
import appsUtil from '../../utils/AppsUtil';
import RestApiUtil from '../../utils/RestApiUtil';
import thorConnectionUtil from '../../utils/ThorConnectionUtil';
import InlineProductModel from '../../containers/Inline/InlineProductModel';
import AppModelMapper from '@ccxi/ccxi-apps/src/utils/modelMapper/appModelMapper';
import {analyticsSharedData, CatalogAnalytics} from '@ccxi/ccxi-apps/src/analytics';
import {ANALYTICS} from '@ccxi/ccxi-apps/src/utils/enums';
import {DIALOG_REPO_KEYS} from '@ccxi/ccxi-apps/src/utils/modelMapper/appModelMapper/confirmationDialog/confirmationDialogConstants';
import ConfirmationDialog from '@ccxi/ccxi-apps/src/utils/modelMapper/appModelMapper/confirmationDialog';
import CCXUIAppConfig from '@ccxi/ccxi-apps/src/stores/config';
import SkeletonModelMapper from '@ccxi/ccxi-apps/src/utils/modelMapper/skeletonModelMapper';
import thorCommonUtil from './ThorCommonUtil';
import TranslationUtil from '@ccxi/ccxi-apps/src/utils/translation';
import {INFORMATION_FIELD_TYPES} from '@ccxi/ccxi-apps/src/utils/pdpUtils/pdpEnums';
import CTAUtil from '../CTAUtil';
import {clientTokenExpiryCheck} from '../ImsUtils';

class ThorUtil {
	thorProductsList: Object;
	sapCodeToSingleAppTile: Object;
	confirmationIDToProductMap: Object;
	formatMessage: Function;
	appUpdateDescPromise: Object;

	constructor() {
		this.sapCodeToSingleAppTile = {};
		this.confirmationIDToProductMap = {};
		this.appUpdateDescPromise = {}; // FFC update desc promise for apps
	}

	initializeWAMClient(thorProductsList: Object, onFinishCallback) {
		if (thorConnectionUtil.shouldforceKaizenExperience()) { // skipping the WAM workflow if Kaizen expereince is forced
			return;
		}
		const initConfigJson = {
			env: window.ENV,
			clientId: getConfigApps('cchome').clientId,
			userContext: {
				accessToken: window.adobeIMS.getAccessToken(),
				email: window.adobeIMS.getUserProfile().email,
				displayName: window.adobeIMS.getUserProfile().displayName
			},
			isLoggingEnabled: appsUtil.hasFeature('temp_enable_wam_logs'),
			shouldTryHttpPing: appsUtil.hasFeature('temp_enable_http_ping'),
			callbacks: {
				onSuccessCallback: ({timeTakenInWAMWF}) => {
					appsUtil.hasFeature('temp_enable_wam_logs') && console.log('Connection with Thor via WAM is established');
					const appsAnalytics = this.getAppsAnalyticsHandler();
					appsAnalytics.onWAMWFSuccess(timeTakenInWAMWF);
					onFinishCallback(true);
				},
				onFailureCallback: (err) => {
					appsUtil.hasFeature('temp_enable_wam_logs') && console.log(`Connection with Thor via WAM could not be established, error is: ${err.error}`);
					onFinishCallback(false);
				},
				onCCDDetectionCallback: (isCCDPresent) => {
					appsUtil.hasFeature('temp_enable_wam_logs') && console.log(`CCD present: ${isCCDPresent}`);
				},
				analyticsCallback: {
					[analyticsEvents.v2ToV1]: (analyticsConfig) => {
						this.sendAnalyticsOnV2ToV1(analyticsConfig);
					},
					[analyticsEvents.onClientWAMConnection]: (analyticsConfig) => {
						this.sendAnalyticsOnWAMConnection(analyticsConfig);
					},
					[analyticsEvents.onGetCurrentDevice]: (analyticsConfig) => {
						this.sendAnalyticsOnGetCurrentDevice(analyticsConfig);
					},
					[analyticsEvents.onThorDetectionByClient]: (analyticsConfig) => {
						this.sendAnalyticsOnThorDetection(analyticsConfig);
					},
					[analyticsEvents.onReceiveCurrentDevice]: (analyticsConfig) => {
						this.sendAnalyticsOnReceiveCurrentDevice(analyticsConfig);
					},
					[analyticsEvents.onClientRegistratonWithWAM]: (analyticsConfig) => {
						this.sendAnalyticsOnRegistrationWithWAM(analyticsConfig);
					},
					[analyticsEvents.onClientRecvThorAppsState]: (analyticsConfig) => {
						this.sendAnalyticsOnReceiveThorAppsState(analyticsConfig);
					},
					[analyticsEvents.onCommandProgress]: () => {},
					[analyticsEvents.onAppUnknown]: () => {}
				},
				onMessageCallback: (thorProductsList) => {
					if (!analyticsSharedData.wamConnected) {
						analyticsSharedData.wamConnected = true;
					}
					if (!appsUtil.isCCXView) {
						return;
					}
					if (thorConnectionUtil.shouldforceKaizenExperience()) { // skipping the WAM workflow if Kaizen expereince is forced
						return;
					}
					if (thorProductsList) {
						this.thorProductsList = thorProductsList;
						if (this.thorProductsList.clientVersion) {
							this.ccdVersion = this.thorProductsList.clientVersion;
						}
						this.setCCXModelFromWAMData(thorProductsList);
					}
				},
				onCloseConfirmationCallback: (confirmationId) => {
					ConfirmationDialog.hideDialog(confirmationId);
				},
				clientTokenExpiryCheck
			}
		};
		wamClient.init(initConfigJson);
		wamClient.attemptConnection(initConfigJson.userContext);
		if (thorProductsList && appsUtil.hasFeature('temp_enable_wam_in_home')) {
			this.thorProductsList = thorProductsList;
			this.setCCXModelFromWAMData(thorProductsList);
		}
	}

	getWAMClient() {
		return wamClient;
	}

	getCCDVersion() {
		return this.ccdVersion;
	}

	isConnected() {
		return this.getWAMClient().isConnected();
	}

	isThorAppsPanelEnabled() {
		return this.getWAMClient().isThorAppsPanelEnabled();
	}

	isAppSupported(sapcode: string, version: string) {
		const ccdProduct = this.getWAMClient().getProduct(sapcode, version);
		return ccdProduct && ccdProduct.stateCategory && ccdProduct.stateCategory.toLowerCase() !== thorAppsStatesCategory.incompatible;
	}

	getAppFromThorAndFFC(ffcAppsList: Object, sapcode: string) {
		let productFromFFC;
		let productFromThor;
		if (sapcode && ffcAppsList) {
			productFromFFC = ffcAppsList[sapcode];
		}
		if (productFromFFC) {
			productFromThor = this.getWAMClient().getProduct(productFromFFC.id, productFromFFC.version);
		}
		return {productFromFFC, productFromThor};
	}

	shouldCCHHandleConfirmationDialog(confirmationDetails: Object) {
		if (confirmationDetails && confirmationDetails.confirmationType) {
			switch (confirmationDetails.confirmationType.toLowerCase()) {
				case thorConfirmationTypes.conflictingProcess:
				case thorConfirmationTypes.fullAppUpdateConfirmationWithAdvancedOptions:
				case thorConfirmationTypes.incompatible:
				case thorConfirmationTypes.launchconfirmation:
				case thorConfirmationTypes.pii:
				case thorConfirmationTypes.productunavailable:
				case thorConfirmationTypes.retryableError:
				case thorConfirmationTypes.uninstallPreferences:
					return true;
			}
		}
		return false;
	}

	getAppsAnalyticsHandler() {
		const appsAnalytics = new AppsAnalytics(window.ENV);
		appsAnalytics.initAnalytics();
		return appsAnalytics;
	}

	/** Analytics functions **/
	sendAnalyticsOnGetCurrentDevice(analyticsConfig: Object) {
		const appsAnalytics = this.getAppsAnalyticsHandler();
		if (analyticsConfig.status) {
			appsAnalytics.onCCHGetCurrentDevice(true);
		}
		else {
			appsAnalytics.onCCHGetCurrentDevice(false);
		}
	}
	sendAnalyticsOnWAMConnection(analyticsConfig: Object) {
		const appsAnalytics = this.getAppsAnalyticsHandler();
		if (analyticsConfig.status) {
			appsAnalytics.onCCHAndWAMConnection(true);
		}
		else {
			appsAnalytics.onCCHAndWAMConnection(false, analyticsConfig.errorType, analyticsConfig.errorDesc);
		}
	}
	sendAnalyticsOnThorDetection(analyticsConfig: Object) {
		const appsAnalytics = this.getAppsAnalyticsHandler();
		if (analyticsConfig.status) {
			appsAnalytics.onCCHToThorDetectionForInline(true);
		}
		else {
			appsAnalytics.onCCHToThorDetectionForInline(false);
		}
	}
	sendAnalyticsOnRegistrationWithWAM(analyticsConfig: Object) {
		const appsAnalytics = this.getAppsAnalyticsHandler();
		if (analyticsConfig.status) {
			appsAnalytics.onCCHRegistrationWithWam(true);
		}
		else {
			appsAnalytics.onCCHRegistrationWithWam(false, analyticsConfig.errorType, analyticsConfig.errorDesc);
		}
	}
	sendAnalyticsOnReceiveCurrentDevice(analyticsConfig: Object) {
		const appsAnalytics = this.getAppsAnalyticsHandler();
		if (analyticsConfig.status) {
			appsAnalytics.onCCHRecvDeviceId(true);
		}
		else {
			appsAnalytics.onCCHRecvDeviceId(false, analyticsConfig.errorType, analyticsConfig.errorDesc);
		}
	}

	sendAnalyticsOnReceiveThorAppsState(analyticsConfig: Object) {
		const appsAnalytics = this.getAppsAnalyticsHandler();
		if (analyticsConfig.status) {
			appsAnalytics.onCCHThorAppsState(true);
		}
		else {
			appsAnalytics.onCCHThorAppsState(false, analyticsConfig.errorType, analyticsConfig.errorDesc);
		}
	}

	sendAnalyticsOnV2ToV1(analyticsConfig: Object) {
		const appsAnalytics = this.getAppsAnalyticsHandler();
		appsAnalytics.onV2ToV1(analyticsConfig.subType);
	}

	setSubHeadingFilter(appModel: Object, subHeadingFilterList: string[]) {
		if (!appModel || !subHeadingFilterList || (subHeadingFilterList.length === 0)) {
			return;
		}

		const existingSubHeadingFilterList = appModel.subHeadingFilter;
		let shouldUpdateList = existingSubHeadingFilterList.length !== subHeadingFilterList.length;

		if (!shouldUpdateList) {
			const filterMaps = {};
			existingSubHeadingFilterList.forEach((existingFilterEle) => {
				filterMaps[existingFilterEle] = true;
			});
			subHeadingFilterList.forEach((newFilterEle) => {
				if (!filterMaps[newFilterEle]) {
					shouldUpdateList = true;
					return;
				}
			});
		}
		if (shouldUpdateList) {
			appModel.setSubHeadingFilter(subHeadingFilterList);
		}
	}

	setAppSectionFilter(appModel: Object, appSectionFilterList: string[]) {
		if (!appModel || !appSectionFilterList || (appSectionFilterList.length === 0)) {
			return;
		}

		const existingAppSectionFilterList = appModel.appSectionFilter;
		const shouldUpdateList = existingAppSectionFilterList.length !== appSectionFilterList.length;

		if (shouldUpdateList) {
			appModel.setAppSectionFilter(appSectionFilterList);
		}
	}

	isActionSupportedOnProduct(sapcode: string, version: string, actionId: string) {
		const thorProduct = this.getWAMClient().getProduct(sapcode, version);
		if (!thorProduct || !thorProduct.stateCategory) {
			return false;
		}
		switch (actionId) {
			case ACTION_ID.INSTALL:
			case ACTION_ID.TRY:
				return thorProduct.stateCategory === thorAppsStatesCategory.availableToInstall;
			case ACTION_ID.UNINSTALL:
			case ACTION_ID.OPEN:
			case ACTION_ID.START_TRIAL:
				switch (thorProduct.stateCategory) {
					case thorAppsStatesCategory.installed:
						return true;
					// break omitted
					case thorAppsStatesCategory.availableToUpdate:
						if (thorProduct.update_type === updateTypes.fullBuildUpdate || thorProduct.update_type === updateTypes.patchUpdate) {
							return true;
						}
					// break omitted
					default:
						return false;
				}
			case ACTION_ID.UPDATE:
				return thorProduct.stateCategory === thorAppsStatesCategory.availableToUpdate;
			case ACTION_ID.CANCEL:
			case ACTION_ID.RETRY:
			case ACTION_ID.PROGRESS_CANCEL:
			case ACTION_ID.ERROR_INFO:
			case ACTION_ID.NOT_COMPATIBLE:
			case ACTION_ID.UPDATE_STATUS:
			case ACTION_ID.SEE_MORE:
				return true;
		}
		return false;
	}

	isUpdateAllSupported() {
		if (!appsUtil.hasFeature('temp_enable_updateAll') || !this.ccdVersion) {
			return false;
		}
		// implement ccd version check here (>=5.2) once ccd starts sending its version in WAM
		const versionParams = this.ccdVersion.split('.');
		if (versionParams && versionParams.length >= 2) {
			const majorVersion = parseInt(versionParams[0]);
			const minorVersion = parseInt(versionParams[1]);
			if (isNaN(majorVersion) || isNaN(minorVersion)) {
				return false;
			}
			if (majorVersion > 5) {
				return true;
			}
			if (majorVersion === 5 && minorVersion >= 2) { // if ccdVersion is >= 5.2
				return true;
			}
		}
		return false;
	}

	isAnOperationHappeningOnProduct(sapcode: string, version: string) {
		const thorProduct = this.getWAMClient().getProduct(sapcode, version);
		if (!thorProduct || !thorProduct.stateCategory) {
			return false;
		}
		return thorProduct.stateCategory === thorAppsStatesCategory.activeSessions;
	}

	onActionForConfirmationBox = (thorProduct: Object, actionId: string, operation: string) => {
		switch (actionId) {
			case ACTION_ID.DONE:
				ConfirmationDialog.hideDialog(thorProduct.confirmationDetails.confirmationID);
				return;
			case ACTION_ID.LEARN_MORE: {
				if (operation === DIALOG_REPO_KEYS.CONFLICT) {
					window.open(`${goUrlBaseLink}${appsUtil.goURL(learnMoreLinkInConflictBase)}`, '_blank');
					return;
				}
				if (operation === DIALOG_REPO_KEYS.UPDATE) {
					window.open(`${goUrlBaseLink}${appsUtil.goURL(learnMoreLinkInUpdateBase)}`, '_blank');
					return;
				}
			}
				break;
		}
		const inlineProductModel = new InlineProductModel(thorProduct.sapcode, thorProduct.version);
		inlineProductModel.sendConfirmation(thorProduct.confirmationDetails.confirmationID, thorProduct.confirmationDetails.confirmationType, actionId, thorProduct.confirmationDetails.confirmationCheckboxes);
		if (thorCommonUtil.shouldShowWaiting(actionId, operation)) {
			thorCommonUtil.setWaiting(thorProduct.sapcode, thorProduct.version);
		}
	}

	isProductEntitled(thorProduct: Object) {
		if (!thorProduct) {
			return false;
		}
		if (thorProduct.stateCategory === thorAppsStatesCategory.updateAllConfirmation) {
			return true;
		}
		let entitled = true;
		if (appsUtil.appsFFCProductList) {
			const config = appsUtil.appsFFCProductList[thorProduct.sapcode];
			if (config && appsUtil.isAppEntitledNew(config, config.id, config.type) && !appsUtil.disableProduct(config.id)) {
				entitled = true;
			}
			else {
				entitled = false;
			}
		}
		if (thorProduct.entitlement_info) {
			if (thorProduct.entitlement_info.status === thorEntitlementInfoType.trialActive
				|| thorProduct.entitlement_info.status === thorEntitlementInfoType.trialAvailable
				|| thorProduct.entitlement_info.status === thorEntitlementInfoType.trialExpired
				|| thorProduct.entitlement_info.status === thorEntitlementInfoType.licenseExpired) {
				entitled = false;
			}
			else {
				entitled = true;
			}
		}
		return entitled;
	}

	isProductFreemium(thorProduct: Object) {
		let isFreemium = false;
		if (appsUtil.appsFFCProductList) {
			const config = appsUtil.appsFFCProductList[thorProduct.sapcode];
			if (config && appsUtil.isAppFreemium(config, config.id, config.type)) {
				isFreemium = true;
			}
		}
		return isFreemium;
	}

	shouldUpdatePDP(sapcode: string, version: string) {
		if (!this.getWAMClient().isConnected()) {
			return false;
		}
		if (appsUtil.isUserEnterpriseUser()) {
			const entitled = this.isProductEntitled(this.getWAMClient().getProduct(sapcode, version));
			if (!entitled) {
				return false;
			}
		}
		const thorProduct = this.getWAMClient().getProduct(sapcode, version);
		if (!thorProduct) {
			return true;
		}
		return this.isCategorySupported({category: thorProduct.stateCategory});
	}

	filterActionList(sapCode: string, buttonList: Object[], surface: string): Object[] {
		// Filter the not allowed CTAs annd Learn More CTA
		const filteredButtonList = buttonList.filter(button =>
			ctaActionIdsDenylist.indexOf(button.actionId) === -1
			&& (surface !== 'pdp' || button.actionId !== ACTION_ID.LEARN_MORE));
		if (appButtonConfig[sapCode].notInPlan && appButtonConfig[sapCode].notInPlan[surface] && appButtonConfig[sapCode].notInPlan[surface].indexOf('buy') > -1) {
			return filteredButtonList;
		}
		return filteredButtonList.filter(button =>
			button.actionId !== ACTION_ID.BUY
			&& button.actionId !== ACTION_ID.BUY_NOW);
	}

	handleLearnMoreInDropDownListForPdp(buttonList: Object[], dropDownList: Object[]): Object[] {
		let dropDownOptionsList = dropDownList;
		if (buttonList.length === 1 && buttonList[0].actionId === ACTION_ID.NOT_COMPATIBLE) {
			dropDownOptionsList.push({
				actionId: ACTION_ID.LEARN_MORE_PDP,
				localizedText: TranslationUtil.getMessage('apps.tile.learnmore'),
				actionType: {
					component : 'dropdown'
				}
			});
		}
		else {
			dropDownOptionsList = dropDownList.filter(button => button.actionId !== ACTION_ID.LEARN_MORE);
		}
		return dropDownOptionsList;
	}

	fillEmptyButtonList(thorProduct, buttonList) {
		if (!thorProduct || !buttonList) {
			return;
		}
		const isAppInstalled = thorProduct.stateCategory === thorAppsStatesCategory.installed
			|| thorProduct.stateCategory === thorAppsStatesCategory.availableToUpdate;
		let appAction;
		if (isAppInstalled) {
			appAction = AppModelMapper.getTileAction({
				actionId: ACTION_ID.OPEN,
				textId: 'ccx.appAction.open'
			});
			// Add else to fix bug: CCH-9856
		}
		const isDisabled = !AppModelMapper.isLaunchable(thorProduct);
		const toolTip = isDisabled ? TranslationUtil.getMessage('apps.tile.disable.open') : '';
		if (appAction) {
			buttonList.push(AppModelMapper.toAppActionModel(
				appAction.actionId,
				'quietButton',
				appAction.localizedText,
				null,
				isDisabled,
				null,
				null,
				toolTip
			));
		}
	}

	getPDPActionList(sapcode: string, version: string) {
		const thorProduct = this.getWAMClient().getProduct(sapcode, version);
		let actionList;
		let buttonList;
		let dropDownList;
		if (thorProduct) {
			actionList = AppModelMapper.toDefaultPageAppActionListModel(thorProduct, thorProduct.stateCategory);
			buttonList = this.filterActionList(thorProduct.sapcode, actionList.buttonList, 'pdp');
			dropDownList = this.filterActionList(thorProduct.sapcode, actionList.dropDownList, 'pdp');
			dropDownList = this.handleLearnMoreInDropDownListForPdp(buttonList, dropDownList);
			if (buttonList && buttonList.length === 0) {
				this.fillEmptyButtonList(thorProduct, buttonList);
			}
		}
		else {
			actionList = this.getActionListForProductsNotFoundInThor(sapcode, version);
			buttonList = this.filterActionList(sapcode, actionList.buttonList, 'pdp');
			dropDownList = this.filterActionList(sapcode, actionList.dropDownList, 'pdp');
			dropDownList = this.handleLearnMoreInDropDownListForPdp(buttonList, dropDownList);
		}
		buttonList.forEach((button) => {
			button.actionType.component = 'button';
			if (button.actionId === ACTION_ID.NOT_COMPATIBLE) {
				button.actionType.variant = 'primary';
				button.actionType.quiet = true;
				button.actionType.icon = 'error';
			}
			if (!thorCommonUtil.shouldSkipVariantCTA(button.actionId)
				|| (button.actionId === ACTION_ID.TRY && buttonList.length === 1)) {
				button.actionType.variant = 'cta';
			}
		});
		dropDownList.forEach(dropDown => dropDown.actionType.component = 'dropdown');
		return [...buttonList, ...dropDownList];
	}

	updatePDPActionList(sapcode: string, version: string) {
		if (appsUtil.CCXUIPDPStore[sapcode] && this.shouldUpdatePDP(sapcode, version)) {
			const actionList = this.getPDPActionList(sapcode, version);
			if (actionList && actionList.length > 0) {
				appsUtil.CCXUIPDPStore[sapcode].setApplicableActionList(actionList);
				actionList.forEach((action) => {
					if (action.actionId === ACTION_ID.NOT_COMPATIBLE && appsUtil.CCXUIPDPStore[sapcode].information) {
						const information = {...appsUtil.CCXUIPDPStore[sapcode].information};
						const informationFieldList = JSON.parse(JSON.stringify(information.informationFieldList));
						informationFieldList.forEach((informationFieldItem) => {
							if (informationFieldItem.actionId === INFORMATION_FIELD_TYPES.COMPATIBILITY) {
								informationFieldItem.fieldValue = false;
								informationFieldItem.icon = 'Alert';
							}
						});
						information.informationFieldList = informationFieldList;
						appsUtil.CCXUIPDPStore[sapcode].setInformation(information);
					}
				});
			}
		}
	}

	isCategorySupported({category, sapcode, version}: {[key: string]: string}) {
		let categoryToVerify = category;
		if (!categoryToVerify) {
			const thorProduct = this.getWAMClient().getProduct(sapcode, version);
			categoryToVerify = thorProduct.stateCategory;
		}
		switch (categoryToVerify) {
			case thorAppsStatesCategory.activeSessions:
			case thorAppsStatesCategory.availableToInstall:
			case thorAppsStatesCategory.availableToUpdate:
			case thorAppsStatesCategory.installed:
			case thorAppsStatesCategory.incompatible:
				return true;
		}
		return false;
	}

	sendAppsRenderAnalyticEvent() {
		if (analyticsSharedData.shouldSendWamAppsRenderEvent) {
			CatalogAnalytics.sendAnalytics(CCXUIAppConfig, undefined, ANALYTICS.EVENTS.APPS_ARRAY, {});
		}
		analyticsSharedData.shouldSendWamAppsRenderEvent = false;
	}

	getActionListForProductsNotFoundInThor(sapCode: string, version: string) {
		const appModel = appsUtil.CCXUIAppStore[`${sapCode}_${version}`];
		let config;
		if (appsUtil.appsFFCProductList) {
			config = appsUtil.appsFFCProductList[sapCode];
		}
		let entitled = false;
		if (config && appsUtil.isAppEntitledNew(config, config.id, config.type)) {
			entitled = true;
		}
		const isFreemium = appsUtil.isAppFreemium(config, config.id, config.type);
		const ctaUtil = new CTAUtil({
			type: 'desktop',
			sapCode,
			entitled,
			displayName: appModel ? appModel.appFamilyName : '',
			hasThor: !appsUtil.isFFCFallback
		});
		let entitlement;
		if (appsUtil.hasFeature('temp_contentful_to_aem_migration') && appsUtil.hasFeature('temp_freemium_apps_aem') && isFreemium) {
			entitlement = subHeadingFilter.freemium;
		}
		else if (!entitled) {
			entitlement = subHeadingFilter.trailApps;
		}
		else {
			entitlement = subHeadingFilter.subscribed;
		}
		this.setSubHeadingFilter(appModel, [entitlement, ...appModel.subSectionFilter]);
		const buttonList = ctaUtil.getButtonList();
		const dropDownList = ctaUtil.getDropDownList();
		return {buttonList, dropDownList};
	}

	fetchUpdateDescription(appModelObject: Object, thorProduct: Object) {
		let platform = RestApiUtil.getPlatformFFCQueryParamFallbackTemp();
		platform = platform.replace('/', '');
		return this.getWAMClient().getUpdateDescription({
			sapCode: thorProduct.sapcode,
			version: thorProduct.version,
			env: window.ENV,
			platform,
			xAdobeAppId: getConfigApps('cchome').clientId,
			locale: appsUtil.locale
		})
			.then((updateDescription) => {
				if (!updateDescription || !updateDescription.UpdateDescription) {
					return;
				}
				const updatesSectionBasedField = appModelObject.appSectionBasedFields.find(({appSectionFilter: appSectionFilterArray}) => appSectionFilterArray.includes('updates'));
				let updateDescriptionList = [];
				if (typeof updateDescription.UpdateDescription === 'string') {
					updateDescriptionList = [updateDescription.UpdateDescription];
				}
				else {
					updateDescriptionList = [...updateDescription.UpdateDescription];
				}
				updatesSectionBasedField.setLocalizedUpdateDescription(updateDescriptionList);
			})
			.catch((e) => {
				appsUtil.printLog(e);
			});
	}

	isCategorySectionBasedField(appSectionBasedField) {
		return appSectionBasedField.appSectionFilter
			&& appSectionBasedField.appSectionFilter.indexOf('updates') === -1
			&& appSectionBasedField.appSectionFilter.indexOf('all') === -1
			&& appSectionBasedField.appSectionFilter.length;
	}

	configureAppForUpdatesSection(appModelObject: Object, subHeading: string, thorProduct: Object, categoryName: string) {
		this.setSubHeadingFilter(appModelObject, [subHeading, subHeadingFilter.installedApps, ...appModelObject.subSectionFilter]);
		(appModelObject.appSectionFilter.indexOf('updates') === -1)
			&& this.setAppSectionFilter(appModelObject, ['updates', ...appModelObject.appSectionFilter, subHeading]);
		thorProduct.updates_info = ''; // eslint-disable-line camelcase

		// setting view more url
		if (thorProduct.update_type === updateTypes.newVersionUpdate
			|| (appsUtil.hasFeature('temp_cch_11432') && thorProduct.update_type === updateTypes.fullBuildUpdate)) {
			appModelObject.productWhatsNewUrl = thorProduct.product_whatsnew_url;
		}
		else {
			thorProduct.product_whatsnew_url = ''; // eslint-disable-line camelcase
		}

		// If updates app section field already present, dont add again
		let updateSectionBasedField = [];
		if (appModelObject.appSectionBasedFields) {
			updateSectionBasedField = appModelObject.appSectionBasedFields.filter(appSectionBasedField => appSectionBasedField.appSectionFilter.indexOf('updates') !== -1);
		}
		!updateSectionBasedField.length && appModelObject.setAppSectionBasedFields([...appModelObject.appSectionBasedFields, AppModelMapper.toUpdateSectionFieldModel(['updates'], thorProduct, thorAppsStatesCategory[categoryName])]);
		if (!this.appUpdateDescPromise[thorProduct.sapcode]) {
			this.appUpdateDescPromise[thorProduct.sapcode] = this.fetchUpdateDescription(appModelObject, thorProduct);
		}
	}

	setCCXModelFromWAMData(thorProductList: Object) {
		if (!thorProductList) {
			return;
		}
		const resetPreviousVersions = (thorProduct) => { // CCh doesn't support previous versions of the apps hence resetting the values.
			thorProduct.previous_version_data = null; // eslint-disable-line camelcase
			thorProduct.other_version_data = null; // eslint-disable-line camelcase
		};

		const handleCTAUpdating = (thorProduct, categoryName) => {
			const appModel = appsUtil.CCXUIAppStore[`${thorProduct.sapcode}_${thorProduct.version}`];
			const mode = thorAppsStatesCategory[categoryName];
			if (thorAppsStatesCategory[categoryName] !== thorAppsStatesCategory.activeSessions) {
				appModel.resetProgress();
				AppModelMapper.resetSubText(appModel);
				AppModelMapper.updateSubtext(appModel, thorProduct, mode);
			};
			const updateAppActionsForCCH = (appActionObj) => {
				const buttonList = this.filterActionList(thorProduct.sapcode, appActionObj.buttonList, 'tile');
				const dropDownList = this.filterActionList(thorProduct.sapcode, appActionObj.dropDownList, 'tile');
				if (buttonList && buttonList.length === 0) {
					this.fillEmptyButtonList(thorProduct, buttonList);
				}
				if (buttonList.length >= 1 && buttonList[buttonList.length - 1]
					&& buttonList[buttonList.length - 1].actionId !== ACTION_ID.NOT_COMPATIBLE) {
					buttonList[buttonList.length - 1].actionType.quiet = false;
				}
				return {buttonList, dropDownList};
			};
			AppModelMapper.updateStatusLabels(appModel, thorProduct, mode);
			appModel.appSectionBasedFields.forEach((appSectionBasedField) => {
				if (!appSectionBasedField) {
					return;
				}

				if (appSectionBasedField.appSectionFilter
					&& appSectionBasedField.appSectionFilter.indexOf('updates') !== -1) {
					const {
						buttonList: updateSectionButtonList,
						dropDownList: updateSectionDropDownList
					} = AppModelMapper.toUpdatePageAppActionListModel(thorProduct, mode);
					appSectionBasedField.setButtonList(updateSectionButtonList);
					appSectionBasedField.setDropDownList(updateSectionDropDownList);
				}
				else if (appSectionBasedField.appSectionFilter
					&& appSectionBasedField.appSectionFilter.indexOf('all') !== -1) {
					const appActionObj = AppModelMapper.toDefaultPageAppActionListModel(thorProduct, mode, appModel);
					const {
						buttonList,
						dropDownList
					} = updateAppActionsForCCH(appActionObj);
					appSectionBasedField.setButtonList(buttonList);
					appSectionBasedField.setDropDownList(dropDownList);
				}
				else {
					const appActionObj = AppModelMapper.toCategoryPageAppActionListModel(thorProduct, mode);
					const {
						buttonList,
						dropDownList
					} = updateAppActionsForCCH(appActionObj);
					appSectionBasedField.setButtonList(buttonList);
					appSectionBasedField.setDropDownList(dropDownList);
				}
			});
		};
		const handleConfirmationBox = (thorProduct) => {
			/* thorProduct.confirmationDetails.config = {
			 mode : 'fullscreen'
			 }; set this for small break points */
			ConfirmationDialog.updateDialogState(thorProduct, (actionId, operation) => {
				this.onActionForConfirmationBox(thorProduct, actionId, operation);
			});
			thorProduct.confirmationDetails.config = {
				disableEscKey : true
			};
			ConfirmationDialog.showOrHideDialog(thorProduct);
		};
		for (const categoryName in thorAppsStatesCategory) {
			if ((thorAppsStatesCategory[categoryName] !== thorAppsStatesCategory.updateAllConfirmation) || this.isUpdateAllSupported()) {
				thorProductList[thorAppsStatesCategory[categoryName]]
				&& thorProductList[thorAppsStatesCategory[categoryName]].forEach((thorProduct) => {
					resetPreviousVersions(thorProduct);
					thorProduct.stateCategory = thorAppsStatesCategory[categoryName];
					const entitled = this.isProductEntitled(thorProduct);
					if (appsUtil.isUserEnterpriseUser() && !entitled) {
						return;
					}
					const isFreemium = this.isProductFreemium(thorProduct);
					const appModelObject = appsUtil.CCXUIAppStore[`${thorProduct.sapcode}_${thorProduct.version}`];
					if (appModelObject && this.isCategorySupported({category: thorAppsStatesCategory[categoryName]})) {
						if (thorProduct.entitlement_info) {
							appModelObject.setEntitlementInfo(thorProduct.entitlement_info);
						}
						if (!thorCommonUtil.onceExecutedViaWAMWorkflow()) {
							thorCommonUtil.setExecutedViaWAMWorkflow();
						}
						else if (thorCommonUtil.isProductWaiting(thorProduct.sapcode, thorProduct.version)) {
							if (thorProduct.status !== wamMsgStatusType.needConfirmation
								&& thorAppsStatesCategory[categoryName] !== thorAppsStatesCategory.activeSessions) {
								return;
							}
						}
						thorCommonUtil.resetWaiting(thorProduct.sapcode, thorProduct.version);
						this.updatePDPActionList(thorProduct.sapcode, thorProduct.version);
						if (thorProduct.status === wamMsgStatusType.needConfirmation
							&& this.shouldCCHHandleConfirmationDialog(thorProduct.confirmationDetails)) {
							handleConfirmationBox(thorProduct);
						}
						else {
							// set isUpdateAvailable flag in appModel
							switch (thorAppsStatesCategory[categoryName]) {
								case thorAppsStatesCategory.activeSessions: {
									// Bug fix: CCH-8081
									/* skipOnErrorActionListUpdate is added to avoid applicable action list update for appmodel
									 In CCW applicable action list is set at the time of app model creation and doesn't require updation in that.
									 The flag is set from CCW
									 */
									thorProduct.skipOnErrorActionListUpdate = true;
									// include the apps that are being currently updated
									// in updates/installed app sections
									if (thorProduct.action === 'update') {
										this.configureAppForUpdatesSection(appModelObject, subHeadingFilter.updatableApps, thorProduct, categoryName);
										handleCTAUpdating(thorProduct, categoryName);
									}
									AppModelMapper.updateWithProgressPacket(appModelObject, thorProduct);
								}
									break;
								case thorAppsStatesCategory.installed: {
									appModelObject.isUpdateAvailable = false;
									// if installed app was updated recently it should be shown in recently updated section
									appModelObject.updateType = undefined;
									if (AppModelMapper.isAppUpdatedRecently({mode: thorAppsStatesCategory.installed, appData: thorProduct})) {
										this.configureAppForUpdatesSection(appModelObject, appsUtil.hasFeature('temp_contentful_to_aem_migration') ? subHeadingFilter.updatedRecently : subHeadingFilter.recentlyUpdated, thorProduct, categoryName);
									}
									else {
										this.setSubHeadingFilter(appModelObject, [subHeadingFilter.installedApps,...appModelObject.subSectionFilter]);
									}
									handleCTAUpdating(thorProduct, categoryName);
								}
									break;
								case thorAppsStatesCategory.availableToUpdate: {
									appModelObject.isUpdateAvailable = thorProduct.update_type === updateTypes.fullBuildUpdate || thorProduct.update_type === updateTypes.newVersionUpdate;
									appModelObject.updateType = thorProduct.update_type;
									this.configureAppForUpdatesSection(appModelObject, subHeadingFilter.updatableApps, thorProduct, categoryName);
									handleCTAUpdating(thorProduct, categoryName);
								}
									break;
								case thorAppsStatesCategory.availableToInstall: {
									appModelObject.isUpdateAvailable = false;
									appModelObject.updateType = undefined;
									let entitlement;
									if (appsUtil.hasFeature('temp_contentful_to_aem_migration') && appsUtil.hasFeature('temp_freemium_apps_aem') && isFreemium) {
										entitlement = subHeadingFilter.freemium;
									}
									else if (!entitled) {
										entitlement = subHeadingFilter.trailApps;
									}
									else {
										entitlement = subHeadingFilter.subscribed;
									}
									this.setSubHeadingFilter(appModelObject, [entitlement, ...appModelObject.subSectionFilter]);
									handleCTAUpdating(thorProduct, categoryName);
								}
									break;
								default:
									appModelObject.isIncompatible = thorAppsStatesCategory[categoryName] === thorAppsStatesCategory.incompatible;
									handleCTAUpdating(thorProduct, categoryName);
							}
						}
					}
					else if (thorProduct.status === wamMsgStatusType.needConfirmation
						&& this.shouldCCHHandleConfirmationDialog(thorProduct.confirmationDetails)) {
						handleConfirmationBox(thorProduct);
					}
				});
			}
		}
		if (appsUtil.appsFFCProductList && appsUtil.isappsFFCProductListLoaded) {
			Object.keys(appsUtil.appsFFCProductList).forEach((key) => {
				if (appsUtil.appsFFCProductList[key]) {
					const sapcode = appsUtil.appsFFCProductList[key].id;
					const version = appsUtil.appsFFCProductList[key].version;
					if (!this.getWAMClient().getProduct(sapcode, version)) {
						const appModel = appsUtil.CCXUIAppStore[`${sapcode}_${version}`];
						const {buttonList, dropDownList} = this.getActionListForProductsNotFoundInThor(sapcode, version);
						appModel.resetProgress();
						AppModelMapper.resetSubText(appModel);
						appModel.appSectionBasedFields.forEach((appSectionBasedField) => {
							appSectionBasedField.setButtonList(buttonList);
							// empty dropdown on category pages
							if (this.isCategorySectionBasedField(appSectionBasedField)) {
								appSectionBasedField.setDropDownList([]);
							}
							else {
								appSectionBasedField.setDropDownList(dropDownList);
							}
						});
						this.updatePDPActionList(sapcode, version);
					}
				}
			});
		}
		this.sendAppsRenderAnalyticEvent();
		if (this.isUpdateAllSupported()) {
			// To handle update count and enable Update all button as per processingState
			SkeletonModelMapper.updateSkeletonStateFromAppsList(thorProductList.processingState);
		}
		else { // set only updates count if updateall is not supported.
			SkeletonModelMapper.setUpdatesCount();
		}
	}
}

export default new ThorUtil();
